import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Input, Radio, Row, Select, Typography, message } from 'antd';
import LilyButton from '../../../../../components/button/lily-button';
import { useSelector } from 'react-redux';
import { StatesOption, formatStateCode } from '../../../../../exports/states-list';
import { useWatch } from 'antd/es/form/Form';
import { useClaimContext } from '../../component/claim-context';
import { getNonEmptyKeys } from '../../../../../exports/util';

const CheckByMail = (props) => {
    const { setDisplayMethod } = props;
    const user = useSelector((state) => state.authenticatedUser);
    const [form] = Form.useForm();
    const { Title, Text } = Typography;

    const { claimForm, setCurrent, setClaimForm } = useClaimContext();
    const selectedOption = useWatch('option', form);
    const [entityRecord, setEntityRecord] = useState({
        option: '',
        recipientName: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
    })

    const populateFields = () => {
        const useUserAddress = form.getFieldValue('option') === 'yes';

        const updatedFields = {
            recipientName: useUserAddress ? (user?.fullName || '') : '',
            address1: useUserAddress ? (user?.address?.line1 || '') : '',
            address2: useUserAddress ? (user?.address?.line2 || '') : '',
            city: useUserAddress ? (user?.address?.city || '') : '',
            state: useUserAddress ? formatStateCode(user?.address?.state) : '',
            zip: useUserAddress ? (user?.address?.zip || '') : '',
        };

        form.setFieldsValue(updatedFields);
    };

    useEffect(() => {
        if (claimForm?.paymentInfo?.option !== 'yes' && claimForm?.paymentInfo?.method === 'check-by-mail') {
            const entity = claimForm?.paymentInfo?.checkByMail || {};
            let record = {
                option: entity?.option || '',
                recipientName: entity?.recipientName || '',
                address1: entity?.address1 || '',
                address2: entity?.address2 || '',
                city: entity?.city || '',
                state: formatStateCode(entity?.state),
                zip: entity?.zip || '',
            }
            setEntityRecord(record);
            form.setFieldsValue(record);
        } else {
            const methodData = user.reimbursementMethods.find(m => m.method === 'check-by-mail');
            let record = {
                option: methodData?.option || '',
                recipientName: methodData?.checkByMail?.recipientName || '',
                address1: methodData?.checkByMail?.address1 || '',
                address2: methodData?.checkByMail?.address2 || '',
                city: methodData?.checkByMail?.city || '',
                state: formatStateCode(methodData?.checkByMail?.state),
                zip: methodData?.checkByMail?.zip || '',
            }
            setEntityRecord(record);
            form.setFieldsValue(record);
        }
    }, [claimForm, user]);

    useEffect(() => {
        if (entityRecord.option && entityRecord.option === selectedOption) {
            form.setFieldsValue(entityRecord);
        } else {
            if (selectedOption === 'yes' || selectedOption === 'no') {
                populateFields();
            }
        }
        form.setFields([
            { name: 'recipientName', errors: [] },
            { name: 'address1', errors: [] },
            { name: 'address2', errors: [] },
            { name: 'city', errors: [] },
            { name: 'state', errors: [] },
            { name: 'zip', errors: [] },
        ]);
    }, [selectedOption]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            setClaimForm((prev) => ({
                ...prev,
                paymentInfo: {
                    ...prev.paymentInfo,
                    method: 'check-by-mail',
                    checkByMail: values,
                },
            }));

            setCurrent(3);
        } catch (err) {
            // message.error('Please complete all required fields.');
        }
    };

    const defaultCheck = useMemo(() => {
        if (selectedOption === 'yes') {
            return true;
        }
        return false
    }, [selectedOption]);

    return (
        <div className='d-flex flex-column gap-4'>
            <div className='d-flex flex-column'>
                <Title className='mb-2 main-title' level={3}>Check Information</Title>
                <Text>Use your current mailing address to receive a physical check.</Text>
            </div>
            <Form
                layout="vertical"
                form={form}
                initialValues={{
                    option: 'yes', // <== fallback here too
                    recipientName: '',
                    address1: '',
                    address2: '',
                    city: '',
                    state: '',
                    zip: '',
                }}
            >
                <Form.Item
                    name="option"
                    label="Would you like to use the address on file?"
                    rules={[{ required: true, message: 'Please select an option.' }]}
                >
                    <Radio.Group className='d-flex flex-row gap-2'>
                        <Radio value="yes">Yes</Radio>
                        <Radio value="no">No</Radio>
                    </Radio.Group>
                </Form.Item>

                <Form.Item
                    label="Recipient Name (as it should appear on check)"
                    name="recipientName"
                    rules={[
                        { required: true, message: 'Please enter recipient name.' },
                        { whitespace: true, message: 'Name cannot be empty.' },
                    ]}
                >
                    <Input disabled={defaultCheck} />
                </Form.Item>

                <Form.Item
                    label="Mailing Address"
                    name="address1"
                    rules={[{ required: true, message: 'Please enter mailing address.' }]}
                >
                    <Input disabled={defaultCheck} />
                </Form.Item>

                <Form.Item
                    label="Mailing Address Line 2"
                    name="address2"
                >
                    <Input disabled={defaultCheck} />
                </Form.Item>

                <Row gutter={16}>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label="City"
                            name="city"
                            rules={[{ required: true, message: 'Please enter city.' }]}
                        >
                            <Input disabled={defaultCheck} />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={8}>
                        <Form.Item
                            label="State"
                            name="state"
                            rules={[
                                { required: true, message: 'Please enter state.' },
                                { pattern: /^[A-Za-z\s]+$/, message: 'State must be alphabetic.' }
                            ]}
                        >
                            <Select showSearch options={StatesOption()} disabled={defaultCheck} />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={8}>
                        <Form.Item
                            label="ZIP"
                            name="zip"
                            rules={[
                                { required: true, message: 'Please enter ZIP code.' },
                                { pattern: /^[0-9]{4,10}$/, message: 'ZIP must be 4 to 10 digits.' }
                            ]}
                        >
                            <Input maxLength={10} disabled={defaultCheck} />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <div className='d-flex flex-row justify-content-end mt-5'>
                <Button
                    style={{ width: '200px' }}
                    color="default"
                    variant="link"
                    onClick={() => {
                        setDisplayMethod('');
                    }}
                >
                    Back
                </Button>
                <LilyButton style={{ width: '200px' }} onClick={handleSubmit} text="Next" />
            </div>
        </div>
    );
};

export default CheckByMail;